"use client";

import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import { getAssignableFunctionFacets } from "@/actions/project-product-context";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { queryKeys } from "@/constants/query";
import { isValidOrgId } from "@/lib/utils";
import type { AssignableFunctionFacet } from "@/types/project-product-context";

const UNASSIGNED_VALUE = "__unassigned__";

export interface ProjectFunctionAssignmentFieldProps {
  readonly value: string | null | undefined;
  readonly onChange: (functionFacetId: string | null) => void;
  readonly onOptionChange?: (option: AssignableFunctionFacet | null) => void;
  /** When set, only functions under this product root are listed (optional context). */
  readonly preferredProductRootId?: string | null;
  readonly disabled?: boolean;
}

function resolveEmptyHint(
  allOptions: readonly AssignableFunctionFacet[],
  visibleOptions: readonly AssignableFunctionFacet[],
  preferredProductRootId: string | null | undefined,
  orgId: string | undefined,
): JSX.Element | null {
  if (allOptions.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No products with functions yet. You can create this project unassigned
        and link it later from the Products hub
        {orgId ? (
          <>
            {" "}
            or{" "}
            <Link
              href={`/${orgId}/products/new`}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              add a product
            </Link>
            .
          </>
        ) : (
          "."
        )}
      </p>
    );
  }

  if (
    preferredProductRootId &&
    visibleOptions.length === 0 &&
    allOptions.length > 0
  ) {
    return (
      <p className="text-xs text-muted-foreground">
        This product has no functions yet. Add functions on the product Grid,
        choose another function below, or leave unassigned.
      </p>
    );
  }

  return null;
}

export function ProjectFunctionAssignmentField({
  value,
  onChange,
  onOptionChange,
  preferredProductRootId = null,
  disabled = false,
}: ProjectFunctionAssignmentFieldProps): JSX.Element {
  const { orgId } = useParams();
  const validOrgId = isValidOrgId(orgId as string | undefined)
    ? (orgId as string)
    : undefined;

  const { data: options = [], isLoading } = useQuery({
    queryKey: queryKeys.assignableFunctionFacets(validOrgId ?? ""),
    queryFn: () => getAssignableFunctionFacets(validOrgId!),
    enabled: !!validOrgId,
    staleTime: 60_000,
  });

  const visibleOptions = useMemo(() => {
    const preferred = preferredProductRootId?.trim();
    if (!preferred) return options;
    return options.filter((option) => option.productRootId === preferred);
  }, [options, preferredProductRootId]);

  const [selectedValue, setSelectedValue] = useState<string>(UNASSIGNED_VALUE);

  useEffect(() => {
    setSelectedValue(value?.trim() ? value : UNASSIGNED_VALUE);
  }, [value]);

  useEffect(() => {
    if (isLoading || !value?.trim()) return;
    const stillVisible = visibleOptions.some(
      (option) => option.functionFacetId === value,
    );
    if (!stillVisible) {
      setSelectedValue(UNASSIGNED_VALUE);
      onChange(null);
      onOptionChange?.(null);
    }
  }, [value, visibleOptions, isLoading, onChange, onOptionChange]);

  const emptyHint = resolveEmptyHint(
    options,
    visibleOptions,
    preferredProductRootId,
    validOrgId,
  );

  return (
    <div className="space-y-2">
      <Label htmlFor="project-function-assignment">Product function</Label>
      <Select
        value={selectedValue}
        disabled={disabled || isLoading}
        onValueChange={(next) => {
          setSelectedValue(next);
          if (next === UNASSIGNED_VALUE) {
            onChange(null);
            onOptionChange?.(null);
            return;
          }
          onChange(next);
          onOptionChange?.(
            visibleOptions.find((option) => option.functionFacetId === next) ??
              options.find((option) => option.functionFacetId === next) ??
              null,
          );
        }}
      >
        <SelectTrigger id="project-function-assignment">
          <SelectValue
            placeholder={
              isLoading ? "Loading functions…" : "Select a function (optional)"
            }
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={UNASSIGNED_VALUE}>Unassigned</SelectItem>
          {visibleOptions.map((option) => (
            <SelectItem
              key={option.functionFacetId}
              value={option.functionFacetId}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {emptyHint ?? (
        <p className="text-xs text-muted-foreground">
          Optional. Links this project to a Function on the product map so it
          appears under that product in the sidebar.
        </p>
      )}
    </div>
  );
}
